<script lang="ts">
  import { onMount } from 'svelte';
  // An icon component rather than U+24D8. The glyph is not in the app's font
  // stack, so it rendered as the missing-character box — which reads as a
  // large question mark and looks like the tooltip is broken rather than the
  // character.
  // Chevrons and the tick come from the same set as the industry picker's, and
  // for the same reason the (i) does: the group header used to open on a text
  // "+" and close on "−", which are a different weight and a different
  // baseline from every other control in the panel.
  import { Info } from 'lucide-svelte';
  import { _ } from 'svelte-i18n';
  import { technologies, mailGaps } from '$lib/stores/selection';
  import GroupedPicker from '$lib/components/GroupedPicker.svelte';
  import { catalogue, catalogueFailed, loadCatalogue } from '$lib/stores/technologies';

  /**
   * `embedded` is the assistant's variant: the same control with room to
   * breathe. The sidebar gets 320px and shows groups closed; a wizard step has
   * the screen and can open the first group and show the explanations in full.
   */
  export let embedded = false;

  /**
   * Called after every tick, so the wizard can re-ask the running total.
   *
   * A prop rather than a reactive statement in the step, which is the
   * convention LegalFormFilters already set here and the better one: the
   * component knows when a choice changed, where a watcher on the store fires
   * once on mount as well and counts a selection nobody made yet.
   */
  export let onChange: (() => void) | undefined = undefined;

  /**
   * The panel's checkbox, in the one shape the bar uses.
   *
   * These were bare `h-3.5 w-3.5` inputs — the browser's own control, square
   * and blue, beside a sidebar full of rounded accent-coloured ones. With 251
   * of them it was the most visible styling in the panel and the only part of
   * it that did not belong.
   *
   * Sized the way ContactFilters sizes its channels: 12px in the 320px rail,
   * where it sits among a dozen sections, and 16px in a guided step, where the
   * same box reads as an afterthought and a 12px hit target is a poor one.
   */
  $: box = embedded ? 'h-4 w-4' : 'h-3 w-3';
  $: checkbox = `${box} rounded border-gray-300 text-accent-600 focus:ring-1 focus:ring-accent-200`;

  /**
   * Whether the filter is switched on at all.
   *
   * The bar's contract: every filter is a checkbox, and its content appears
   * only once that is ticked. Eleven groups and 251 checkboxes unfurled by
   * default is most of the sidebar spent on a filter most customers do not
   * want, which is what the contract exists to prevent.
   *
   * # It is forced on, never off
   *
   * The other filters here derive the tick from their store — CapitalFilters
   * unticks itself when the capital clears. That works because ticking them
   * writes a value. Ticking this one writes nothing: it reveals a list to
   * choose from. Derived the same way it would untick itself on the frame
   * after it was ticked, and the panel would not open.
   *
   * So the store only ever turns it on, which is what a filter pill or the
   * assistant setting technologies needs, and turning it off is the customer's
   * click alone — which clears, below.
   */
  let enabled = false;

  $: if ($technologies.length > 0 || $mailGaps.length > 0) enabled = true;

  function toggleEnabled() {
    enabled = !enabled;
    if (!enabled) {
      // Unticking is "do not filter by this", so it has to take the selection
      // with it. Leaving it set would keep narrowing the search from a control
      // that now reads as off.
      technologies.set([]);
      mailGaps.set([]);
      onChange?.();
    }
  }

  onMount(loadCatalogue);

  let search = '';
  /** The gaps' explanation showing, by slug; the picker keeps its own. */
  let explaining: string | null = null;
  $: needle = search.trim().toLowerCase();

  $: groups = $catalogue?.groups ?? [];
  $: pickerGroups = groups.map((g) => ({ key: g.key, label: g.label, items: g.technologies }));
  $: gaps = $catalogue?.mail_gaps ?? [];

  /**
   * The grouped gaps become one dropdown each; the ungrouped ones stay boxes.
   *
   * A domain has exactly one DMARC posture and one SPF posture, so a row of
   * checkboxes was inviting two answers to the same question and spending five
   * lines of the panel on a subject most customers skip. Two selects and a box
   * say the same thing in three.
   *
   * Order comes from the catalogue, which puts the sellable option first in
   * each group — "DMARC nicht durchgesetzt" ahead of the two specific ways it
   * can be missing.
   */
  $: gapGroups = [...new Set(gaps.filter((g) => g.group).map((g) => g.group))].map((key) => ({
    key,
    options: gaps.filter((g) => g.group === key)
  }));
  $: looseGaps = gaps.filter((g) => !g.group);

  /** Which option of a group is chosen, or '' for "beliebig". */
  function chosenInGroup(key: string | undefined, selected: string[]): string {
    const option = gaps.find((g) => g.group === key && selected.includes(g.slug));
    return option?.slug ?? '';
  }

  /**
   * Choosing a gap. The two kinds behave differently and used to share one
   * path, which is where "Eigener Mailserver" could be switched on and never
   * off.
   *
   * # An ungrouped gap is its own checkbox
   *
   * So it toggles, and leaves every other gap alone. The shared version
   * computed the survivors as "every selected slug whose group is not the one
   * being changed" — and for an ungrouped gap the group *is* undefined, so that
   * test dropped every ungrouped gap there was, the clicked one included, and
   * then appended the clicked slug straight back. The result was always
   * selected, whichever state it started in.
   *
   * It went unnoticed because the only ungrouped gap for most of this
   * control's life was exchange_on_prem, which was never offered.
   *
   * # A group holds one state at most
   *
   * Two states of one setting can never both hold, so choosing replaces
   * whatever that group had. Letting both in would build a query asking for a
   * domain with no DMARC record whose DMARC record says p=none, which matches
   * nothing and says nothing about why. An empty slug clears the group — that
   * is how its checkbox switches off.
   *
   * Either way the gaps outside the group being changed survive untouched.
   */
  function pickGap(key: string | undefined, slug: string) {
    if (key === undefined) {
      mailGaps.set(toggle($mailGaps, slug));
      onChange?.();
      return;
    }

    const others = $mailGaps.filter((s) => {
      const gap = gaps.find((g) => g.slug === s);
      return gap?.group !== key;
    });
    mailGaps.set(slug ? [...others, slug] : others);
    onChange?.();
  }

  /**
   * A group's own checkbox, because a dropdown has no off.
   *
   * The select used to carry a "beliebig" entry, which was the only way to stop
   * filtering on DMARC once you had started. That reads as a value rather than
   * as off: the control still looks engaged, it sits alongside the other
   * filters' checkboxes saying the opposite, and nothing about it suggests that
   * one of its options is the way out. Every other filter in this panel is a
   * checkbox that reveals its content, so these are too.
   *
   * Ticking selects the group's first option rather than revealing an empty
   * select. A checkbox that turns something on and then filters nothing is the
   * bug this replaces, one level down — and the first option is the broad one
   * ("Nicht abgesichert"), with the two specific ways it can be missing after
   * it, so the obvious choice is also the one worth leading with. See
   * dnstech.Gaps, where that order is deliberate.
   */
  function toggleGapGroup(key: string | undefined) {
    const lead = gaps.find((g) => g.group === key);
    pickGap(key, chosenInGroup(key, $mailGaps) ? '' : (lead?.slug ?? ''));
  }

  function toggle(list: string[], slug: string): string[] {
    return list.includes(slug) ? list.filter((s) => s !== slug) : [...list, slug];
  }

  function pickTechnology(slug: string) {
    technologies.set(toggle($technologies, slug));
    onChange?.();
  }
</script>

<!--
  The technology filters, for the sidebar and the assistants both.

  # One component, two places, because the rule is the hard part

  What a selection means is not obvious and cannot be left for the customer to
  infer: several tools inside a group are OR — Mailchimp *or* Brevo — and
  several groups are AND. Written twice, the two copies would explain it
  differently within a month, and the panel would disagree with the wizard about
  what the customer had just asked for.

  # What is chosen is shown in the bar, not here

  The selection used to be repeated as chips at the top of this control, which
  was a second place showing the same thing and the wrong one: every other
  filter puts what it applied in the bar above the results, where a customer
  can see the whole selection at once and drop any part of it without opening
  a panel.

  One pill per technology there, for the same reason legal forms get one each —
  six were chosen one at a time and the wrong one has to be droppable on its
  own.

  # Why the list comes from the server

  244 entries in eleven groups, and the grouping decides the query's meaning. A
  copy here would drift from the vocabulary the query understands, and the
  failure is silent: the server drops a slug it does not recognise, the customer
  ticks a box, the count does not move, and nothing anywhere says why.
-->
<div class={embedded ? 'space-y-4' : enabled ? 'm-2 rounded-control bg-surface-well p-3' : 'p-3'}>
  {#if !embedded}
    <!--
      The bar's contract, in the shape CapitalFilters and FoundingFilters
      already use. Absent in a guided step, where ScreenView has just asked
      "Möchten Sie nach eingesetzten Technologien filtern?" and a checkbox
      underneath would be the same question twice.
    -->
    <div class="mb-3">
      <label class="group flex cursor-pointer items-center">
        <input type="checkbox" checked={enabled} on:click={toggleEnabled} class="mr-2 {checkbox}" />
        <svg class="filter-mark mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
          />
        </svg>
        <span
          class="text-xs font-semibold text-gray-900 transition-colors group-hover:text-gray-700"
        >
          {$_('filters.technologies.title')}
        </span>
      </label>
    </div>
  {/if}

  {#if !enabled && !embedded}
    <!-- Ticked open, or nothing. -->
  {:else if $catalogueFailed}
    <!--
      Deliberately not an empty list. Nothing to show and "there are no
      technologies to filter on" are different statements, and only one of them
      is true here.
    -->
    <p class="text-xs text-gray-500">{$_('filters.technologies.unavailable')}</p>
  {:else if groups.length === 0}
    <p class="text-xs text-gray-400">{$_('filters.technologies.loading')}</p>
  {:else}
    <GroupedPicker
      groups={pickerGroups}
      selected={$technologies}
      onPick={pickTechnology}
      placeholder={$_('filters.technologies.search')}
      anyOf={$_('filters.technologies.anyOf')}
      {embedded}
      bind:search
    />

    {#if gaps.length > 0 && !needle}
      <!--
        Kept apart from the technologies above, and after them.

        Everything above says "this company uses X", which is a claim about
        evidence we hold. These say the opposite — we asked and found nothing —
        which is a different kind of statement and the only one here that can be
        wrong in a way the customer cannot see. Mixed into the same list they
        would read as eleven more products.
      -->
      <div class="mt-4">
        <!-- The weight the group headers above now carry, so this reads as a
             peer of them rather than as a stray line of body text. -->
        <h4 class="mb-1 text-sm font-semibold text-ink-800">
          {$_('filters.technologies.gapsTitle')}
        </h4>
        <p class="mb-2 text-xs text-ink-500">{$_('filters.technologies.gapsHint')}</p>

        {#each gapGroups as group (group.key)}
          {@const chosenSlug = chosenInGroup(group.key, $mailGaps)}
          <div class="mb-2">
            <label class="flex cursor-pointer items-center py-0.5 text-sm">
              <input
                type="checkbox"
                class="mr-2 {checkbox}"
                checked={chosenSlug !== ''}
                on:change={() => toggleGapGroup(group.key)}
              />
              <span class="text-gray-600">
                {$_(`filters.technologies.gapGroup.${group.key}`)}
              </span>
            </label>

            {#if chosenSlug !== ''}
              <!--
                Its own accessible name, because the group label now belongs to
                the checkbox. A select whose only label is a sibling checkbox is
                announced as "DMARC" twice, and the second one is unlabelled.
              -->
              <select
                aria-label={$_('filters.technologies.gapState', {
                  values: { group: $_(`filters.technologies.gapGroup.${group.key}`) }
                })}
                class="ml-6 mt-1 w-[calc(100%-1.5rem)] rounded-control border border-hairline bg-surface-panel px-2 py-1 text-sm"
                value={chosenSlug}
                on:change={(event) => pickGap(group.key, event.currentTarget.value)}
              >
                {#each group.options as option (option.slug)}
                  <option value={option.slug} title={option.why}>{option.label}</option>
                {/each}
              </select>

              {#if embedded}
                <!--
                  The chosen option's explanation, printed where there is room
                  for it. A select cannot carry a tooltip per option in any way
                  a customer will find.
                -->
                {@const chosen = group.options.find((o) => o.slug === chosenSlug)}
                {#if chosen}
                  <p class="ml-6 mt-1 text-xs text-gray-400">{chosen.why}</p>
                {/if}
              {/if}
            {/if}
          </div>
        {/each}

        {#each looseGaps as gap (gap.slug)}
          <!--
            Behind an (i), not printed underneath.

            Embedded, this used to set the sentence out in full below the box.
            For the grouped gaps that still happens and earns its place: those
            print only once a state has been chosen, so the text answers a
            question the customer has just asked. This one printed
            unconditionally — four lines of German under a single checkbox,
            before anybody had shown any interest in it — and the section it
            sits in is the one the panel deliberately gives least weight to.

            The same control the technology rows use, so the explanation is
            still a keystroke away rather than hover-only.
          -->
          <div class="flex items-start py-0.5 text-sm">
            <label class="flex min-w-0 flex-1 cursor-pointer items-start">
              <input
                type="checkbox"
                class="mr-2 mt-0.5 {checkbox}"
                checked={$mailGaps.includes(gap.slug)}
                on:change={() => pickGap(undefined, gap.slug)}
              />
              <span class="min-w-0 text-gray-600">{gap.label}</span>
            </label>

            {#if gap.why}
              <button
                type="button"
                class="ml-1 shrink-0 rounded-control px-1 py-0.5 text-gray-400 transition hover:text-accent-600"
                aria-label={gap.why}
                on:mouseenter={() => (explaining = gap.slug)}
                on:mouseleave={() => (explaining = null)}
                on:focus={() => (explaining = gap.slug)}
                on:blur={() => (explaining = null)}
              >
                <Info class="h-3.5 w-3.5" />
              </button>
            {/if}
          </div>

          {#if explaining === gap.slug && gap.why}
            <p class="ml-6 pb-1 text-xs text-ink-500">{gap.why}</p>
          {/if}
        {/each}
      </div>
    {/if}
  {/if}
</div>
